// KPI Catalog (v1 - local prototype)
// A catalog item describes a KPI "kind" (what it measures and which chart(s) can render it).
// Project-level KPI instances reference these catalog items and bind them to project-specific data.

export type KpiCategoryKey = "schedule" | "finance" | "mission" | "data_reliability";
export type KpiChartType = "bar" | "line";

export type KpiCatalogItem = {
  kpi_catalog_guid: string; // stable id for the KPI kind
  title: string;            // catalog title (generic)
  category: KpiCategoryKey;
  // Optional: allow a KPI kind to appear in multiple categories (e.g., Progress works for Schedule and Finance).
  // Keep `category` as the primary/home category for backward compatibility.
  categories?: KpiCategoryKey[];
  description?: string;
  allowed_main_charts: KpiChartType[];
  allowed_history_charts: KpiChartType[];
};

// Minimal starter catalog. We will expand this once we add the KPI Marketplace / SDK concept.
export const kpiCatalog: KpiCatalogItem[] = [
  {
    kpi_catalog_guid: "kpi_sched_progress_v1",
    title: "Progress",
    category: "schedule",
    categories: ["schedule"],
    description: "Percent complete vs baseline/forecast over time.",
    allowed_main_charts: ["bar", "line"],
    allowed_history_charts: ["line"],
  },
  {
    kpi_catalog_guid: "kpi_sched_variance_v1",
    title: "Schedule Variance",
    category: "schedule",
    description: "Schedule variance indicator (SPI / days ahead/behind).",
    allowed_main_charts: ["bar"],
    allowed_history_charts: ["line"],
  },
  {
    kpi_catalog_guid: "kpi_cost_progress_v1",
    title: "Budget Progress",
    category: "finance",
    categories: ["finance"],
    description: "Cumulative budget vs actual spend over time.",
    allowed_main_charts: ["bar", "line"],
    allowed_history_charts: ["line"],
  },
  {
    kpi_catalog_guid: "kpi_cost_variance_v1",
    title: "Cost Variance",
    category: "finance",
    description: "Cost variance indicator (CPI / budget vs actual).",
    allowed_main_charts: ["bar"],
    allowed_history_charts: ["line"],
  },
  {
    kpi_catalog_guid: "kpi_mission_outcome_v1",
    title: "Mission Outcome",
    category: "mission",
    description: "Outcome progress (project-defined).",
    allowed_main_charts: ["bar"],
    allowed_history_charts: ["line"],
  },
  {
    kpi_catalog_guid: "kpi_data_freshness_v1",
    title: "Data Freshness",
    category: "data_reliability",
    description: "Timeliness/freshness of reported data.",
    allowed_main_charts: ["bar"],
    allowed_history_charts: ["line"],
  },
];

/**
 * KPI category display labels (English fallback).
 *
 * For i18n-aware rendering, prefer using `t('common:kpiCategory.schedule')` etc.
 * at the component level via react-i18next's useTranslation hook.
 * This map remains as a non-React fallback for engine/utility code.
 */
export const categoryLabels: Record<KpiCategoryKey, string> = {
  schedule: "Schedule",
  finance: "Budget",
  mission: "Mission",
  data_reliability: "Data Reliability",
};
